import React, { useState, useRef, useEffect } from "react";
import "./../managerPage/css/card.css";
import "./../managerPage/css/table.css";
import "./../managerPage/css/form.css";
import "./../managerPage/css/dropdown.css";
import { IoIosSearch } from "react-icons/io";
import { RiDeleteBin6Line } from "react-icons/ri";
import { IoIosCloseCircle } from "react-icons/io";
import { LuDownload } from "react-icons/lu";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import Select from "react-select";
import Tippy from "@tippyjs/react";
import { useGetRepairReportsQuery } from "../../slices/maintenanceApiSlice"; // Import the query hook
import { toast } from "sonner";
import { Eye } from "lucide-react";

const SAdminRReport = () => {
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedRows, setSelectedRows] = useState([]);
  const [modalData, setModalData] = useState(null);
  const [editableData, setEditableData] = useState({
    Additional_cost: "",
    Additional_Hour: "",
    Remarks: "",
  });

  // Fetch repair reports data using RTK Query
  const {
    data: apiData,
    isLoading,
    isError,
    error,
  } = useGetRepairReportsQuery();

  const rowsPerPage = 10;

  // Process API data to normalize it to expected format based on the actual API response
  const processApiData = (rawData) => {
    if (!rawData) return [];

    // Check if the data is an array
    const dataArray = Array.isArray(rawData) ? rawData : [rawData];

    return dataArray.map((item, index) => {
      // Create a properly formatted object mapping the API response fields
      // to the expected component fields
      return {
        rid: item.repairReportID || `#${1000 + index}`,
        assetName: "Asset", // This field is not present in the API response
        startTime: item.startTime || "N/A",
        endTime: item.endTime || "N/A",
        Date: item.finishedDate || new Date().toLocaleDateString(),
        Area: "N/A", // This field is not present in the API response
        Total_cost: `NU.${item.totalCost || 0}`,
        part_used: item.partsUsed || "N/A",
        location: "N/A", // This field is not present in the API response
        description: "N/A", // This field is not present in the API response
        total_technician: item.technicians
          ? item.technicians.split(",").length.toString()
          : "0",
        Assigned_supervisor: "N/A", // This field is not present in the API response
        Assigned_Technician: item.technicians || "N/A",
        Additional_information: item.information || "",
        imageUrl: item.images || [],

        // Store original data for reference
        original: item,
      };
    });
  };

  // Format date from YYYY-MM-DD to DD-MM-YYYY
  const formatDate = (dateString) => {
    if (!dateString) return "";

    const [year, month, day] = dateString.split("-");
    return `${day}-${month}-${year}`;
  };

  // Get processed data
  const data = processApiData(apiData);

  // Log the structure of the API data and processed data for debugging
  console.log("Original API data:", apiData);
  console.log("Processed data:", data);

  useEffect(() => {
    if (isLoading) {
      toast.loading("Loading repair reports...", {
        id: "loading-repair-reports",
      });
    } else {
      toast.dismiss("loading-repair-reports");
    }
  }, [isLoading]);

  const sortedData = [...data].sort((a, b) => {
    // We want newest reports first, but we don't have a proper way to determine this
    // without a timestamp field, so we'll use the ID for now
    return b.rid.localeCompare(a.rid);
  });

  const filteredData = sortedData.filter((item) => {
    return Object.values(item).some(
      (value) =>
        value &&
        typeof value === "string" &&
        value.toLowerCase().includes(searchTerm.toLowerCase()),
    );
  });

  const totalPages = Math.ceil(filteredData.length / rowsPerPage);
  const displayedData = filteredData.slice(
    (currentPage - 1) * rowsPerPage,
    currentPage * rowsPerPage,
  );

  const handleSelectRow = (rid) => {
    setSelectedRows((prevSelectedRows) =>
      prevSelectedRows.includes(rid)
        ? prevSelectedRows.filter((item) => item !== rid)
        : [...prevSelectedRows, rid],
    );
  };

  const handleView = (item) => {
    setModalData(item);
  };

  const handleCloseModal = () => {
    setModalData(null);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setEditableData((prev) => ({ ...prev, [name]: value }));
  };

  // Ref for the modal
  const modalRef = useRef(null);

  // Function to handle PDF download
  const handleDownloadPDF = () => {
    if (!modalData) return; // Prevent function execution if no data is selected

    const doc = new jsPDF();

    // Add title
    doc.text("Repair Report", 14, 15);

    // Define table headers
    const columns = ["Field", "Value"];

    // Map modalData dynamically into table rows
    const rows = [
      ["Report ID", modalData.rid || "N/A"],
      ["Start Time", modalData.startTime || "N/A"],
      ["End Time", modalData.endTime || "N/A"],
      ["Finished Date", formatDate(modalData.Date) || "N/A"],
      ["Total Cost", modalData.Total_cost || "N/A"],
      ["Parts Used", modalData.part_used || "N/A"],
      ["Technicians", modalData.Assigned_Technician || "N/A"],
      ["Additional Information", modalData.Additional_information || "N/A"],
      ["Repair ID", modalData.original?.repairID || "N/A"],
    ];

    // Generate the table using autoTable
    autoTable(doc, {
      head: [columns],
      body: rows,
      startY: 20,
      theme: "grid",
      styles: { fontSize: 10 },
      headStyles: { fillColor: [41, 128, 185] }, // Blue header background
    });

    // Save the PDF
    doc.save(`Repair_Report_${modalData.rid}.pdf`);
  };

  // download selected reports as CSV
  const handleDownloadSelected = () => {
    if (selectedRows.length === 0) return;

    const selectedData = data.filter((item) => selectedRows.includes(item.rid));

    const csvContent = [
      [
        "Report ID",
        "Repair ID",
        "Start Time",
        "End Time",
        "Finished Date",
        "Total Cost",
        "Parts Used",
        "Technicians",
        "Information",
      ],
      ...selectedData.map((item) => [
        item.rid || "",
        item.original?.repairID || "",
        item.startTime || "",
        item.endTime || "",
        formatDate(item.Date) || "",
        item.Total_cost || "",
        item.part_used || "",
        item.Assigned_Technician || "",
        item.Additional_information || "",
      ]),
    ]
      .map((row) =>
        row.map((value) => `"${String(value).replace(/"/g, '""')}"`).join(","),
      )
      .join("\n");

    const blob = new Blob([csvContent], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "repair_report.csv";
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  };

  if (isLoading) {
    return <div className="loading">Loading repair reports...</div>;
  }

  if (isError) {
    return (
      <div className="error">
        Error loading repair reports: {error?.message || "Unknown error"}
      </div>
    );
  }

  return (
    <div className="p-4 sm:py-6 sm:px-8 space-y-4">
      {/* Header */}
      <div className="space-y-2 mb-4">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 sm:gap-4">
          <div className="flex items-center gap-3">
            <h1 className="text-xl sm:text-2xl font-bold text-zinc-800">
              Repair Report
            </h1>
          </div>
        </div>
        <p className="text-zinc-600">
          View and analyze repair maintenance reports and statistics
        </p>
      </div>

      <div className="">
        <div className="">
          <div className="flex flex-col lg:flex-row gap-4 mb-6 lg:items-center lg:justify-between">
            <div className="flex flex-col sm:flex-row gap-4 w-full lg:w-auto">
              {/* Search Input */}
              <div className="w-full lg:flex-1 lg:min-w-60">
                <div className="relative">
                  <input
                    type="text"
                    placeholder="Search repair reports..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full p-3 border border-gray-300 rounded-lg pl-10 text-sm sm:text-base"
                  />
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <svg
                      className="h-4 w-4 sm:h-5 sm:w-5 text-gray-400"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                      />
                    </svg>
                  </div>
                </div>
              </div>
            </div>

            <div className="flex flex-col sm:flex-row gap-4 w-full lg:w-auto">
              <button
                onClick={handleDownloadSelected}
                className="inline-flex items-center px-4 py-2 bg-[#305845] text-white text-sm font-medium rounded-lg hover:bg-[#2a4d3a] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#305845] transition-colors"
              >
                <LuDownload className="w-4 h-4 mr-2" />
                Download
              </button>
            </div>
          </div>
          <div className="table-container">
            <div className="bg-white rounded-lg shadow overflow-hidden">
              <div className="overflow-x-auto min-w-0">
                <table className="w-full min-w-max">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        <input
                          type="checkbox"
                          checked={
                            selectedRows.length === displayedData.length &&
                            displayedData.length > 0
                          }
                          onChange={() =>
                            setSelectedRows(
                              selectedRows.length === displayedData.length
                                ? []
                                : displayedData.map((item) => item.rid),
                            )
                          }
                          className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                        />
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Report ID
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Start Time
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        End Time
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Date
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Total Cost
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Parts Used
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Technicians
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Information
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Action
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {displayedData.map((item, index) => (
                      <tr key={item.rid || index} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap">
                          <input
                            type="checkbox"
                            checked={selectedRows.includes(item.rid)}
                            onChange={() => handleSelectRow(item.rid)}
                            className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                          />
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {item.rid}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {item.startTime}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {item.endTime}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {formatDate(item.Date)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {item.Total_cost}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {item.part_used}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {item.Assigned_Technician}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {item.Additional_information}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                          <button
                            className="inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md text-blue-700 bg-blue-100 hover:bg-blue-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                            onClick={() => handleView(item)}
                          >
                            <Eye size={16} className="mr-2" />
                            View
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          {/* Pagination */}
          {displayedData.length > 0 && (
            <div className="pagination">
              <span>{filteredData.length} Results</span>
              <div>
                {totalPages > 0 &&
                  [...Array(Math.min(totalPages, 5)).keys()].map((num) => (
                    <button
                      key={num}
                      className={currentPage === num + 1 ? "active" : ""}
                      onClick={() => setCurrentPage(num + 1)}
                    >
                      {num + 1}
                    </button>
                  ))}
                {totalPages > 5 && (
                  <>
                    <span>...</span>
                    <button onClick={() => setCurrentPage(totalPages)}>
                      {totalPages}
                    </button>
                  </>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Modal for Viewing Request */}
        {modalData && (
          <div className="modal-overlay">
            <div className="modal-content" ref={modalRef}>
              {/* Close Button */}
              <div className="modal-header">
                <h2 className="form-h">Repair Report</h2>
                <button className="close-btn" onClick={handleCloseModal}>
                  <IoIosCloseCircle
                    style={{ color: "#897463", width: "20px", height: "20px" }}
                  />
                </button>
              </div>
              <div className="modal-body" ref={modalRef}>
                <form className="repair-form">
                  <div className="modal-content-field">
                    <label>Report ID:</label>
                    <input type="text" value={modalData.rid || ""} readOnly />
                  </div>

                  <div className="modal-content-field">
                    <label>Repair ID:</label>
                    <input
                      type="text"
                      value={modalData.original?.repairID || ""}
                      readOnly
                    />
                  </div>

                  <div className="modal-content-field">
                    <label>Time</label>
                    <div className="time-input">
                      <input
                        type="text"
                        value={modalData.startTime || ""}
                        readOnly
                      />
                      <input
                        type="text"
                        value={modalData.endTime || ""}
                        readOnly
                      />
                    </div>
                  </div>
                  <div className="modal-content-field">
                    <label>Finished Date</label>
                    <input
                      type="text"
                      value={formatDate(modalData.Date) || "Not completed"}
                      readOnly
                    />
                  </div>

                  <div className="modal-content-field">
                    <label>Parts used: </label>
                    <input
                      type="text"
                      value={modalData.part_used || ""}
                      readOnly
                    />
                  </div>
                  <div className="modal-content-field">
                    <label>Total Technicians</label>
                    <input
                      type="text"
                      value={modalData.total_technician || ""}
                      readOnly
                    />
                  </div>
                  <div className="modal-content-field">
                    <label>Technicians</label>
                    <input
                      type="text"
                      value={modalData.Assigned_Technician || ""}
                      readOnly
                    />
                  </div>
                  <div className="modal-content-field">
                    <label>Total Cost: </label>
                    <input
                      type="text"
                      value={modalData.Total_cost || ""}
                      readOnly
                    />
                  </div>
                  <div className="modal-content-field">
                    <label>Information: </label>
                    <textarea
                      value={modalData.Additional_information || ""}
                      readOnly
                    />
                  </div>

                  <div className="modal-content-field">
                    <label>Repair Images:</label>
                    <div className="TModal-profile-img">
                      {Array.isArray(modalData.imageUrl) &&
                      modalData.imageUrl.length > 0 ? (
                        modalData.imageUrl.map((imgSrc, index) => (
                          <img
                            key={index}
                            src={imgSrc}
                            alt={`Work Order ${index + 1}`}
                            className="TModal-modal-image"
                          />
                        ))
                      ) : modalData.imageUrl ? (
                        // If `imageUrl` is a string, display it as a single image
                        <img
                          src={modalData.imageUrl}
                          alt="Work Order"
                          className="TModal-modal-image"
                        />
                      ) : (
                        <p>No image available</p>
                      )}
                    </div>
                  </div>

                  <div className="modal-buttons">
                    <button
                      type="button"
                      className="accept-btn"
                      onClick={handleDownloadPDF}
                    >
                      Download
                      <LuDownload style={{ marginLeft: "12px" }} />
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default SAdminRReport;
